import {
  CommentMode,
  DashboardState,
  DeviceType,
  EDatasourceType,
  EScreenType,
  ESdChangeReason,
  EUserAccessPart,
  IDevice,
  IFillStyle,
  ImageLibraryListType,
  IScreenDesign,
  IStylingBorder,
  IStylingEffects,
  IStylingPosition,
  IStylingShadow,
  IStylingTypography,
  LeftPanelType,
  RightPanelType,
  StylingMode,
  ViewPart,
  Visibility,
  WarningPanelType
} from "@kuika/kuika-cl-model";
import { loadedHelpdeskProvider } from "../../../components/design-tools/helpdesk/helpdesk-config";
import { isReportDesignerEnabled } from "../../../components/design-tools/report-designer/report-designer-feature-gate";
import { IDatabaseDiagramData } from "../../../models/interface/database-diagram-data";
import { DataDiagramSchemeService } from "../../../services/data-diagram-scheme-service";
import { notifyDesignerLiveToolIdle } from "../../../services/designer-live/designer-live";
import { DesignerPresenceReporter } from "../../../services/designer-live/designer-presence";
import confData from "../../../shared/data/conf.json";
import deviceListJson from "../../../shared/data/device-list.json";
import { PartialContextProvider } from "../../../shared/patial/partial-context-provider";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { IScreenDesignContextState } from "../screen-design-context";
import { IScreenDesignContextProviderProps, ScreenDesignContextProvider } from "../screen-design-context-provider";

export interface IEnterReportDesignerOptions {
  /**
   * Open on the landing (the report list) rather than on the report that is being loaded. Entry from
   * another tool asks for the landing; paths that are opening one named report (project load of a
   * report, the Issues panel) do not.
   */
  showLanding: boolean;
}

export interface DesignSituationProviderState {
  stylingActiveKeys: string[];
  dashboardState: DashboardState;
  setDashboardState: (state: DashboardState) => void;
  toolbarIsHidden: boolean;
  designViewLastClickedLeftPanel: LeftPanelType;
  reportDesignerViewLastClickedLeftPanel: LeftPanelType;
  workFlowViewLastClickedLeftPanel: LeftPanelType;
  zendeskViewLastClickedLeftPanel: LeftPanelType;
  scheduleViewLastClickedLeftPanel: LeftPanelType;
  /**
   * Which left panel the Document Parameters tool is showing. Defaults to Datasources: source actions are database
   * actions, so the panel that creates them is the one worth having open on arrival — there is no sub-entity list
   * of this tool's own to show instead.
   */
  documentParametersViewLastClickedLeftPanel: LeftPanelType;
  apiManagementViewLastClickedLeftPanel: LeftPanelType;
  branchingViewLastClickedLeftPanel: LeftPanelType;
  imageLibraryListType: ImageLibraryListType;
  getActiveLeftPanel: () => LeftPanelType;
  getActiveImageLibraryListType: () => ImageLibraryListType;
  designViewLastClickedRightPanel: RightPanelType;
  workFlowViewLastClickedRightPanel: RightPanelType;
  reportDesignerViewLastClickedRightPanel: RightPanelType;
  dataDiagramViewLastClickedRightPanel: RightPanelType;
  codeViewLastClickedRightPanel: RightPanelType;
  getActiveRightPanel: () => RightPanelType;
  setActiveRightPanelByClick: (val: RightPanelType) => void;
  setActiveLeftPanelByClick: (val: LeftPanelType) => void;
  setActiveWorkFlowLeftPanel: (val: LeftPanelType) => void;
  setActiveWorkFlowRightPanel: (val: RightPanelType) => void;
  setActiveScheduledJobsPanel: (val: LeftPanelType) => void;
  setActiveApiManagementPanel: (val: LeftPanelType) => void;
  setActiveBranchingPanel: (val: LeftPanelType) => void;
  enterReportDesigner: (options: IEnterReportDesignerOptions) => void;
  setActiveImageLibraryList: (val: ImageLibraryListType) => void;
  handleHideOrShowToolbar: (val: boolean) => void;
  isTypographyStyleShow: boolean;
  setTypographyStyleShow: (val: boolean) => void;
  isBorderStyleShow: boolean;
  setBorderStyleShowFunc: (val: boolean) => void;
  isShadowStyleShow: boolean;
  setShadowStyleShowFunc: (val: boolean) => void;
  setStylingActiveKeys: (keys: string[]) => void;
  selectedDevice: IDevice;
  selectedDeviceType: DeviceType;
  setSpecificDevice: (device: IDevice, refershRequired: boolean) => void;
  setDeviceType: (deviceType: DeviceType) => void;
  toogleDeviceType: (deviceType: DeviceType) => void;
  setResolution: (width: number) => void;
  getStylingMode: () => StylingMode;
  getCommentMode: () => CommentMode;
  getVisibility: (viewPart: ViewPart) => Visibility;
  databaseDiagramData: IDatabaseDiagramData;
  isDatabaseDiagramDataLoading: boolean;
  updateDatabaseDiagramData: () => void;
  warningPanelType: WarningPanelType;
  getWarningPanelType: () => WarningPanelType;
  setWarningPanelType: (val: WarningPanelType) => void;
  setCustomPositionStyleBackup: (backupCustomPosition: IStylingPosition | undefined) => void;
  backupCustomPosition?: IStylingPosition;
  setCustomShadowStyleBackup: (backupCustomShadow: IStylingShadow | undefined) => void;
  backupCustomShadow?: IStylingShadow;
  setCustomBorderStyleBackup: (backupCustomBorder: IStylingBorder | undefined) => void;
  backupCustomBorder?: IStylingBorder;
  setCustomTypographyStyleBackup: (backupCustomTypography: IStylingTypography | undefined) => void;
  backupCustomTypography?: IStylingTypography;
  setCustomFillStyleBackup: (backupCustomFill: IFillStyle | undefined) => void;
  backupCustomFill?: IFillStyle;
  setCustomEffectsStyleBackup: (backupCustomEffects: IStylingEffects | undefined) => void;
  backupCustomEffects?: IStylingEffects;
  notificationTemplateActivePanel?: string;
  setNotificationTemplateActivePanel: (key: string) => void;
  zendeskActivePanel?: string;
  setActiveZendeskPanel: (key: string) => void;
  isLeftPanelCollapsed: { [key: number]: boolean };
  setLeftPanelCollapsed: (value: boolean) => void;
  collapseLeftPanel: () => void;
  isRightPanelCollapsed: { [key: number]: boolean };
  setRightPanelCollapsed: (value: boolean) => void;
  chatDockedToPanel: "right" | null;
  setChatDockedToPanel: (panel: "right" | null) => void;
  // Whether the FLOATING Kuika AI chat is open. Lives here, not in DesignLeftMenu, because the
  // top-bar trigger (header/toolbar-right/ai-chat-button.tsx) has to toggle it and paint its
  // active state — a sibling region cannot read the left menu's local state.
  isMcpChatVisible: boolean;
  setIsMcpChatVisible: (value: boolean) => void;
}

const { kuikaDevelopers } = confData;
const deviceList: IDevice[] = deviceListJson as unknown as IDevice[];

// Every ViewPart that is an entry point into the Report Designer. When
// REPORT_DESIGNER_ENABLED=false these are forced Hidden (see getVisibility).
const REPORT_DESIGNER_VIEW_PARTS: ViewPart[] = [
  ViewPart.ReportDesignerButton,
  ViewPart.ReportsButton,
  ViewPart.ReportDesignerPagesPanel,
  ViewPart.ReportSizesDropdown
];

// The Zendesk side panels of the full-page Helpdesk, hidden when the Kuika ITSM helpdesk fills the
// centre with its own list and ticket (see isItsmHelpdeskShown).
const ITSM_HELPDESK_HIDDEN_VIEW_PARTS: ViewPart[] = [
  ViewPart.LeftPanel,
  ViewPart.ZendeskPanel,
  ViewPart.RightPanel,
  ViewPart.ZendeskPropertiesPanel
];

export class DesignSituationProvider extends PartialContextProvider<
  ScreenDesignContextProvider,
  IScreenDesignContextProviderProps,
  IScreenDesignContextState,
  DesignSituationProviderState
> {
  static defaultState: DesignSituationProviderState = {
    stylingActiveKeys: ["1", "2", "3"],
    dashboardState: DashboardState.design,
    setDashboardState: () => {},
    toolbarIsHidden: false,
    isTypographyStyleShow: false,
    isBorderStyleShow: false,
    isShadowStyleShow: false,
    designViewLastClickedLeftPanel: LeftPanelType.Component,
    reportDesignerViewLastClickedLeftPanel: LeftPanelType.Component,
    workFlowViewLastClickedLeftPanel: LeftPanelType.WorkFlowList,
    zendeskViewLastClickedLeftPanel: LeftPanelType.Zendesk,
    // Nothing resident: the jobs list IS the tool's landing page now. Only the
    // on-demand datasources panel opens here, via the icon rail.
    scheduleViewLastClickedLeftPanel: LeftPanelType.None,
    // The tool is entered from the Document Parameters node in the sources tree, and that tree is what stays. It was
    // Datasources (the custom-actions list) while the entry point was the toolbar and there was no panel to preserve;
    // now that default swaps the panel out from under the node the user just clicked.
    documentParametersViewLastClickedLeftPanel: LeftPanelType.DataDiagramScheme,
    apiManagementViewLastClickedLeftPanel: LeftPanelType.ApiManagement,
    branchingViewLastClickedLeftPanel: LeftPanelType.Branching,
    imageLibraryListType: ImageLibraryListType.Multiple,
    getActiveLeftPanel: () => {
      return LeftPanelType.Component;
    },
    getActiveImageLibraryListType: () => {
      return ImageLibraryListType.Multiple;
    },
    designViewLastClickedRightPanel: RightPanelType.Properties,
    reportDesignerViewLastClickedRightPanel: RightPanelType.Properties,
    workFlowViewLastClickedRightPanel: RightPanelType.Properties,
    dataDiagramViewLastClickedRightPanel: RightPanelType.Properties,
    codeViewLastClickedRightPanel: RightPanelType.Comments,
    getActiveRightPanel: () => {
      return RightPanelType.Properties;
    },
    setActiveRightPanelByClick: () => {},
    setActiveLeftPanelByClick: () => {},
    setActiveWorkFlowLeftPanel: () => {},
    setActiveWorkFlowRightPanel: () => {},
    setActiveScheduledJobsPanel: () => {},
    setActiveApiManagementPanel: () => {},
    setActiveBranchingPanel: () => {},
    enterReportDesigner: () => {},
    setActiveImageLibraryList: () => {},
    handleHideOrShowToolbar: () => {},
    setTypographyStyleShow: () => {},
    setBorderStyleShowFunc: () => {},
    setShadowStyleShowFunc: () => {},
    setStylingActiveKeys: () => {},
    selectedDevice: deviceList[0],
    selectedDeviceType: DeviceType.Desktop,
    setSpecificDevice: () => {},
    setDeviceType: () => {},
    toogleDeviceType: () => {},
    setResolution: () => {},
    getStylingMode: () => {
      return StylingMode.Default;
    },
    getCommentMode: () => {
      return CommentMode.SelectedDisplayItem;
    },
    getVisibility: () => {
      return {} as Visibility;
    },
    databaseDiagramData: {} as IDatabaseDiagramData,
    isDatabaseDiagramDataLoading: false,
    updateDatabaseDiagramData: () => {},
    warningPanelType: WarningPanelType.Close,
    getWarningPanelType: () => {
      return WarningPanelType.Close;
    },
    setWarningPanelType: () => {},
    setCustomPositionStyleBackup: () => {},
    setCustomShadowStyleBackup: () => {},
    setCustomBorderStyleBackup: () => {},
    setCustomTypographyStyleBackup: () => {},
    setCustomFillStyleBackup: () => {},
    setCustomEffectsStyleBackup: () => {},
    notificationTemplateActivePanel: "1",
    setNotificationTemplateActivePanel: () => {},
    zendeskActivePanel: "4",
    setActiveZendeskPanel: () => {},
    isLeftPanelCollapsed: {},
    setLeftPanelCollapsed: () => {},
    collapseLeftPanel: () => {},
    isRightPanelCollapsed: {},
    setRightPanelCollapsed: () => {},
    chatDockedToPanel: null,
    setChatDockedToPanel: () => {},
    isMcpChatVisible: false,
    setIsMcpChatVisible: () => {}
  };

  getProviderValues = (): DesignSituationProviderState => {
    return {
      stylingActiveKeys: this.state.stylingActiveKeys,
      dashboardState: this.state.dashboardState,
      setDashboardState: this.setDashboardState,
      toolbarIsHidden: this.state.toolbarIsHidden,
      isTypographyStyleShow: this.state.isTypographyStyleShow,
      isBorderStyleShow: this.state.isBorderStyleShow,
      isShadowStyleShow: this.state.isShadowStyleShow,
      designViewLastClickedLeftPanel: this.state.designViewLastClickedLeftPanel,
      reportDesignerViewLastClickedLeftPanel: this.state.reportDesignerViewLastClickedLeftPanel,
      workFlowViewLastClickedLeftPanel: this.state.workFlowViewLastClickedLeftPanel,
      zendeskViewLastClickedLeftPanel: this.state.zendeskViewLastClickedLeftPanel,
      scheduleViewLastClickedLeftPanel: this.state.scheduleViewLastClickedLeftPanel,
      documentParametersViewLastClickedLeftPanel: this.state.documentParametersViewLastClickedLeftPanel,
      apiManagementViewLastClickedLeftPanel: this.state.apiManagementViewLastClickedLeftPanel,
      branchingViewLastClickedLeftPanel: this.state.branchingViewLastClickedLeftPanel,
      imageLibraryListType: this.state.imageLibraryListType,
      getActiveLeftPanel: this.getActiveLeftPanel,
      getActiveImageLibraryListType: this.getActiveImageLibraryListType,
      designViewLastClickedRightPanel: this.state.designViewLastClickedRightPanel,
      reportDesignerViewLastClickedRightPanel: this.state.reportDesignerViewLastClickedRightPanel,
      workFlowViewLastClickedRightPanel: this.state.workFlowViewLastClickedRightPanel,
      dataDiagramViewLastClickedRightPanel: this.state.dataDiagramViewLastClickedRightPanel,
      codeViewLastClickedRightPanel: this.state.codeViewLastClickedRightPanel,
      getActiveRightPanel: this.getActiveRightPanel,
      setActiveRightPanelByClick: this.setActiveRightPanelByClick,
      setActiveLeftPanelByClick: this.setActiveLeftPanelByClick,
      setActiveWorkFlowLeftPanel: this.setActiveWorkFlowLeftPanel,
      setActiveWorkFlowRightPanel: this.setActiveWorkFlowRightPanel,
      setActiveScheduledJobsPanel: this.setActiveScheduledJobsPanel,
      setActiveApiManagementPanel: this.setActiveApiManagementPanel,
      setActiveBranchingPanel: this.setActiveBranchingPanel,
      enterReportDesigner: this.enterReportDesigner,
      setActiveImageLibraryList: this.setActiveImageLibraryList,
      handleHideOrShowToolbar: this.handleHideOrShowToolbar,
      setTypographyStyleShow: this.setTypographyStyleShow,
      setBorderStyleShowFunc: this.setBorderStyleShow,
      setShadowStyleShowFunc: this.setShadowStyleShow,
      setStylingActiveKeys: this.setStylingActiveKeys,
      selectedDevice: this.state.selectedDevice,
      selectedDeviceType: this.state.selectedDeviceType,
      setSpecificDevice: this.setSpecificDevice,
      setDeviceType: this.setDeviceType,
      toogleDeviceType: this.toogleDeviceType,
      setResolution: this.setResolution,
      getStylingMode: this.getStylingMode,
      getCommentMode: this.getCommentMode,
      getVisibility: this.getVisibility,
      databaseDiagramData: this.state.databaseDiagramData,
      isDatabaseDiagramDataLoading: this.state.isDatabaseDiagramDataLoading,
      updateDatabaseDiagramData: this.updateDatabaseDiagramData,
      warningPanelType: this.state.warningPanelType,
      getWarningPanelType: this.getWarningPanelType,
      setWarningPanelType: this.setWarningPanelType,
      setCustomPositionStyleBackup: this.setCustomPositionStyleBackup,
      backupCustomPosition: this.state.backupCustomPosition,
      setCustomShadowStyleBackup: this.setCustomShadowStyleBackup,
      backupCustomShadow: this.state.backupCustomShadow,
      setCustomBorderStyleBackup: this.setCustomBorderStyleBackup,
      backupCustomBorder: this.state.backupCustomBorder,
      setCustomTypographyStyleBackup: this.setCustomTypographyStyleBackup,
      backupCustomTypography: this.state.backupCustomTypography,
      setCustomFillStyleBackup: this.setCustomFillStyleBackup,
      backupCustomFill: this.state.backupCustomFill,
      setCustomEffectsStyleBackup: this.setCustomEffectsStyleBackup,
      backupCustomEffects: this.state.backupCustomEffects,
      notificationTemplateActivePanel: this.state.notificationTemplateActivePanel,
      setNotificationTemplateActivePanel: this.setNotificationTemplateActivePanel,
      zendeskActivePanel: this.state.zendeskActivePanel,
      setActiveZendeskPanel: this.setActiveZendeskPanel,
      isLeftPanelCollapsed: this.state.isLeftPanelCollapsed,
      setLeftPanelCollapsed: this.setLeftPanelCollapsed,
      collapseLeftPanel: this.collapseLeftPanel,
      isRightPanelCollapsed: this.state.isRightPanelCollapsed,
      setRightPanelCollapsed: this.setRightPanelCollapsed,
      chatDockedToPanel: this.state.chatDockedToPanel,
      setChatDockedToPanel: this.setChatDockedToPanel,
      isMcpChatVisible: this.state.isMcpChatVisible,
      setIsMcpChatVisible: this.setIsMcpChatVisible
    };
  };

  /**
   * What the panel's own collapse control does — "collapse" meaning close, wherever the left
   * panel is ON-DEMAND rather than resident.
   *
   * On the workflow editor the datasources panel is opened from the icon rail and its visibility
   * gate reads `activeLeftPanel`, never the collapse map (see the ViewPart.LeftPanel case), so
   * setting the flag there hid nothing — the control looked broken — and there would have been no
   * way back either, because the edge expand tab is suppressed for the same reason. Clearing the
   * active panel (None renders nothing — see the ViewPart.LeftPanel gate) is what a second click
   * on the rail button does too, through `handleLeftPanelRailClick`, so the two ways of closing
   * it agree.
   *
   * Workflow is now the ONLY view that works this way. Scheduled Jobs and the API Builder host the
   * same on-demand panel but their gates DO read the collapse map (KOA-18379), so they take the
   * branch below and behave like every resident panel: the flag hides it and the edge tab brings
   * it back. Do not add a state here without making its gate ignore the flag as well — that
   * mismatch is the bug, in either direction.
   */
  collapseLeftPanel = (): void => {
    if (this.state.dashboardState === DashboardState.workFlow) {
      if (this.getActiveLeftPanel() === LeftPanelType.Datasources) {
        this.setState({ workFlowViewLastClickedLeftPanel: LeftPanelType.None });
      }
      return;
    }
    this.setLeftPanelCollapsed(true);
  };

  setLeftPanelCollapsed = (value: boolean) => {
    const { isLeftPanelCollapsed, dashboardState } = this.state;
    this.setState({
      isLeftPanelCollapsed: {
        ...isLeftPanelCollapsed,
        [dashboardState]: value
      }
    });
  };

  setRightPanelCollapsed = (value: boolean) => {
    const { isRightPanelCollapsed, dashboardState } = this.state;
    this.setState({
      isRightPanelCollapsed: {
        ...isRightPanelCollapsed,
        [dashboardState]: value
      }
    });
  };

  setChatDockedToPanel = (panel: "right" | null) => {
    this.setState({ chatDockedToPanel: panel });
  };

  setIsMcpChatVisible = (isMcpChatVisible: boolean) => {
    this.setState({ isMcpChatVisible });
  };

  setCustomPositionStyleBackup = (backupCustomPosition: IStylingPosition | undefined) => {
    this.setState({ backupCustomPosition });
  };

  setCustomShadowStyleBackup = (backupCustomShadow: IStylingShadow | undefined) => {
    this.setState({ backupCustomShadow });
  };

  setCustomBorderStyleBackup = (backupCustomBorder: IStylingBorder | undefined) => {
    this.setState({ backupCustomBorder });
  };

  setCustomTypographyStyleBackup = (backupCustomTypography: IStylingTypography | undefined) => {
    this.setState({ backupCustomTypography });
  };

  setCustomFillStyleBackup = (backupCustomFill: IFillStyle | undefined) => {
    this.setState({ backupCustomFill });
  };

  setCustomEffectsStyleBackup = (backupCustomEffects: IStylingEffects | undefined) => {
    this.setState({ backupCustomEffects });
  };

  getStylingMode = (): StylingMode => {
    if (this.state.hasASelectedComponent === true) {
      return StylingMode.Default;
    }
    return StylingMode.Screen;
  };

  getCommentMode = (): CommentMode => {
    if (
      this.state.dashboardState === DashboardState.dataDiagramScheme ||
      this.state.dashboardState === DashboardState.workFlow
    ) {
      return CommentMode.Screen;
    }
    return CommentMode.SelectedDisplayItem;
  };

  setResolution = (width: number): void => {
    let deviceType: DeviceType = DeviceType.Tablet;
    if (width <= 480) {
      deviceType = DeviceType.Phone;
    } else if (width >= 1200) {
      deviceType = DeviceType.Desktop;
    }
    this.setState({ selectedDeviceType: deviceType });
  };

  getActiveLeftPanel = (dashboardState?: DashboardState): LeftPanelType => {
    let result: LeftPanelType = LeftPanelType.Component;
    if (this.state.dashboardState === DashboardState.design) {
      result = this.state.designViewLastClickedLeftPanel;
    }
    if (
      this.state.dashboardState === DashboardState.reportDesigner ||
      dashboardState === DashboardState.reportDesigner
    ) {
      result = this.state.reportDesignerViewLastClickedLeftPanel;
    }
    if (
      this.state.dashboardState === DashboardState.dataDiagramScheme ||
      this.state.dashboardState === DashboardState.aiHub
    ) {
      result = LeftPanelType.DataDiagramScheme;
    }
    if (this.state.dashboardState === DashboardState.workFlow) {
      result = this.state.workFlowViewLastClickedLeftPanel;
    }
    if (this.state.dashboardState === DashboardState.configManager) {
      result = LeftPanelType.ConfigManager;
    }
    if (this.state.dashboardState === DashboardState.scheduledJobs) {
      result = this.state.scheduleViewLastClickedLeftPanel;
    }
    if (this.state.dashboardState === DashboardState.documentParameters) {
      result = this.state.documentParametersViewLastClickedLeftPanel;
    }
    if (this.state.dashboardState === DashboardState.apiManagement) {
      result = this.state.apiManagementViewLastClickedLeftPanel;
    }
    if (this.state.dashboardState === DashboardState.notificationTemplate) {
      result = LeftPanelType.NotificationTemplate;
    }
    if (this.state.dashboardState === DashboardState.zendesk) {
      result = LeftPanelType.Zendesk;
    }
    if (this.state.dashboardState === DashboardState.branching) {
      result = LeftPanelType.Branching;
    }
    return result;
  };

  getActiveImageLibraryListType = (): ImageLibraryListType => {
    // let result: ImageLibraryListType = ImageLibraryListType.Multiple;
    const result = this.state.imageLibraryListType;
    return result;
  };

  getWarningPanelType = (): WarningPanelType => {
    const result = this.state.warningPanelType;
    return result;
  };

  setWarningPanelType = (val: WarningPanelType) => {
    if (val === this.getWarningPanelType()) {
      return;
    }
    this.setState({ warningPanelType: val });
  };

  getActiveRightPanel = (): RightPanelType => {
    let result: RightPanelType = RightPanelType.Properties;
    const { doesSelectedComponentHasStyleProp } = this.state;
    if (this.state.dashboardState === DashboardState.design) {
      result = this.state.designViewLastClickedRightPanel;
      if (result === RightPanelType.Styling && doesSelectedComponentHasStyleProp === false) {
        result = RightPanelType.Properties;
      }
    }
    // Explore Documentation is removed (2026-08-14). Defaults that landed on it land on
    // Properties, and the RightPanel container case below hides the panel in the states where
    // documentation was its only content — so no default here can surface an empty frame.
    if (this.state.dashboardState === DashboardState.reportDesigner) {
      result = this.state.reportDesignerViewLastClickedRightPanel;
      if (this.partial.reportDesignProvider.isReportDesignerLandingVisible()) {
        result = RightPanelType.Properties;
      }
      if (result === RightPanelType.Styling && doesSelectedComponentHasStyleProp === false) {
        result = RightPanelType.Properties;
      }
    }
    if (this.state.dashboardState === DashboardState.workFlow) {
      if (!this.state.selectedWf?.id) {
        result = RightPanelType.Properties;
      } else {
        result = this.state.workFlowViewLastClickedRightPanel;
      }
    }
    if (
      this.state.dashboardState === DashboardState.dataDiagramScheme ||
      this.state.dashboardState === DashboardState.aiHub
    ) {
      result = this.state.dataDiagramViewLastClickedRightPanel;
    }

    if (
      this.state.dashboardState === DashboardState.configManager ||
      this.state.dashboardState === DashboardState.runSettings
    ) {
      result = RightPanelType.Properties;
    }

    if (this.state.dashboardState === DashboardState.apiManagement) {
      if (!this.state.activeApiMethodId) {
        result = RightPanelType.Properties;
      } else {
        result = RightPanelType.Properties;
      }
    }

    if (this.state.dashboardState === DashboardState.notificationTemplate) {
      result = RightPanelType.Properties;
    }

    if (this.state.dashboardState === DashboardState.scheduledJobs) {
      if (!this.state.selectedSjId) {
        result = RightPanelType.Properties;
      } else {
        result = RightPanelType.Properties;
      }
    }

    if (
      this.state.dashboardState === DashboardState.dataDiagramScheme ||
      this.state.dashboardState === DashboardState.aiHub
    ) {
      if (
        (this.state.activeEditingDatasource?.datasourceType === EDatasourceType.BleService ||
          this.state.activeCreatingDatasourceType === EDatasourceType.BleService) &&
        !this.state.activeBluetoothMethodId
      ) {
        result = RightPanelType.Properties;
      } else {
        result = RightPanelType.Properties;
      }
    }

    return result;
  };

  isUserKuikaDeveloper = (username: string): boolean => {
    return KMainFunctions.isUserKuikaDeveloper(username);
  };

  setActiveRightPanelByClick = (val: RightPanelType) => {
    if (val === this.getActiveRightPanel()) {
      return;
    }
    if (val !== this.getActiveRightPanel() && this.state.isRightPanelCollapsed[this.state.dashboardState] === true) {
      // this.setRightPanelCollapsed(false);
    }
    if (this.state.dashboardState === DashboardState.workFlow) {
      this.setState({ workFlowViewLastClickedRightPanel: val });
    } else if (
      this.state.dashboardState === DashboardState.dataDiagramScheme ||
      this.state.dashboardState === DashboardState.aiHub
    ) {
      this.setState({ dataDiagramViewLastClickedRightPanel: val });
    } else if (this.state.dashboardState === DashboardState.reportDesigner) {
      this.setState({ reportDesignerViewLastClickedRightPanel: val });
    } else {
      this.setState({ designViewLastClickedRightPanel: val });
    }
  };

  setActiveLeftPanelByClick = (val: LeftPanelType) => {
    // Re-selecting the active panel stays a no-op everywhere, the workflow editor included:
    // programmatic callers ("Edit SQL Action", the MCP left-panel tool) mean "open", and a
    // toggle here closed the panel that hosts the action editor. Closing is collapseLeftPanel's.
    if (val === this.getActiveLeftPanel()) {
      return;
    }
    if (val !== this.getActiveLeftPanel() && this.state.isLeftPanelCollapsed[this.state.dashboardState] === true) {
      // this.setLeftPanelCollapsed(false);
    }
    if (this.state.dashboardState === DashboardState.design) {
      this.setState({ designViewLastClickedLeftPanel: val });
    } else if (this.state.dashboardState === DashboardState.reportDesigner) {
      this.setState({ reportDesignerViewLastClickedLeftPanel: val });
    } else if (this.state.dashboardState === DashboardState.workFlow) {
      this.setState({ workFlowViewLastClickedLeftPanel: val });
    } else if (this.state.dashboardState === DashboardState.scheduledJobs) {
      this.setState({ scheduleViewLastClickedLeftPanel: val });
    } else if (this.state.dashboardState === DashboardState.documentParameters) {
      this.setState({ documentParametersViewLastClickedLeftPanel: val });
    } else if (this.state.dashboardState === DashboardState.apiManagement) {
      this.setState({ apiManagementViewLastClickedLeftPanel: val });
    } else if (this.state.dashboardState === DashboardState.branching) {
      this.setState({ branchingViewLastClickedLeftPanel: val });
    }
  };

  setActiveWorkFlowLeftPanel = (val: LeftPanelType) => {
    if (this.state.dashboardState != DashboardState.workFlow) {
      this.setState({ dashboardState: DashboardState.workFlow }, () => {
        this.setState({ workFlowViewLastClickedLeftPanel: val });
      });
    } else this.setState({ workFlowViewLastClickedLeftPanel: val });
  };

  setActiveWorkFlowRightPanel = (val: RightPanelType) => {
    if (this.state.dashboardState != DashboardState.workFlow) {
      this.setState({ dashboardState: DashboardState.workFlow }, () => {
        this.setState({ workFlowViewLastClickedRightPanel: val });
      });
    } else this.setState({ workFlowViewLastClickedRightPanel: val });
  };

  setActiveScheduledJobsPanel = (val: LeftPanelType) => {
    if (this.state.dashboardState != DashboardState.scheduledJobs) {
      this.setState({ dashboardState: DashboardState.scheduledJobs }, () => {
        this.setState({ scheduleViewLastClickedLeftPanel: val });
      });
    } else this.setState({ scheduleViewLastClickedLeftPanel: val });
  };

  setActiveApiManagementPanel = (val: LeftPanelType) => {
    if (this.state.dashboardState != DashboardState.apiManagement) {
      this.setState({ dashboardState: DashboardState.apiManagement }, () => {
        this.setState({ apiManagementViewLastClickedLeftPanel: val });
      });
    } else this.setState({ apiManagementViewLastClickedLeftPanel: val });
  };

  setActiveBranchingPanel = (val: LeftPanelType) => {
    if (this.state.dashboardState != DashboardState.branching) {
      this.setState({ dashboardState: DashboardState.branching }, () => {
        this.setState({ branchingViewLastClickedLeftPanel: val });
      });
    } else this.setState({ branchingViewLastClickedLeftPanel: val });
  };

  /**
   * The one door into the Report Designer. The tool, its Reports panel and its landing are written
   * in a single setState — partitions share one state object — so no render ever sees report mode
   * without the landing decided. Entry paths used to make these three calls themselves, each a
   * little differently, and the toolbar's unsaved-changes branch made only the first: the UI screen
   * that was loaded then rendered on the report canvas with no report selected.
   *
   * Already in the tool, only the landing moves: the user's left panel stays where they put it.
   */
  enterReportDesigner = (options: IEnterReportDesignerOptions): void => {
    // Kill-switch: the same coercion setDashboardState applies to every other entry. Keep the two in
    // lockstep — they are the only writers of `dashboardState = reportDesigner`.
    if (!isReportDesignerEnabled()) {
      this.setDashboardState(DashboardState.design);
      return;
    }

    if (this.state.dashboardState === DashboardState.reportDesigner) {
      this.setState({
        isCreateReportDesignPageActive: options.showLanding,
        changeReason: ESdChangeReason.OtherSetStates
      });
      return;
    }

    const isEnteringEditableCanvas: boolean = !this.isEditableCanvasState(this.state.dashboardState);
    this.setState(
      {
        dashboardState: DashboardState.reportDesigner,
        reportDesignerViewLastClickedLeftPanel: LeftPanelType.Reports,
        isCreateReportDesignPageActive: options.showLanding,
        // See setDashboardState: the popup belongs to the surface it was opened from.
        isDocumentParametersPopupOpen: false,
        changeReason: ESdChangeReason.OtherSetStates
      },
      (): void => this.afterToolSwitch(DashboardState.reportDesigner, isEnteringEditableCanvas)
    );
  };

  setActiveImageLibraryList = (val: ImageLibraryListType) => {
    if (
      this.state.dashboardState === DashboardState.design ||
      this.state.dashboardState === DashboardState.reportDesigner
    ) {
      this.setState({ imageLibraryListType: val });
    }
  };

  isSelectedDeviceType(deviceType: DeviceType) {
    const selectedDevice: IDevice = this.getSelectedDevice();
    if (
      (selectedDevice.type === "Desktop" && deviceType === DeviceType.Desktop) ||
      (selectedDevice.type === "Tablet" && deviceType === DeviceType.Tablet) ||
      (selectedDevice.type === "Phone" && deviceType === DeviceType.Phone)
    ) {
      return true;
    }
    return false;
  }

  getSelectedDevice = (): IDevice => {
    return this.state.selectedDevice ? this.state.selectedDevice : deviceList[0];
  };

  getDefaultDeviceKeyByType = (deviceType: DeviceType): string => {
    switch (deviceType) {
      case DeviceType.Phone: {
        return "iPhoneX";
      }
      case DeviceType.Tablet: {
        return "iPadPro";
      }
      case DeviceType.Desktop: {
        return "none";
      }
      default: {
        return "none";
      }
    }
  };

  toogleDeviceType = (deviceType: DeviceType) => {
    const key: string = this.getDefaultDeviceKeyByType(deviceType);
    if (this.isSelectedDeviceType(deviceType) === false) {
      this.setDeviceByKey(key);
    } else {
      this.setDeviceByKey("none");
    }
  };

  setDeviceByKey = (key: string) => {
    if (deviceList && deviceList.length > 0) {
      const devices = deviceList.filter((device: IDevice) => device.id === key);
      if (devices && devices.length > 0) {
        this.setSpecificDevice(devices[0]);
      }
    }
  };

  /**
   * Arms the canvas resize transition for one device switch, then disarms it.
   *
   * The transition cannot simply live on the iframe: its width and height are written on
   * every reveal and on every save (the frame collapses to 0x0 when hidden and is restored
   * when shown), so a standing rule animated the canvas growing out of nothing each time it
   * came back. Only a deliberate device change should read as a resize, and this is the sole
   * path into one — both the Desktop/Tablet/Phone tabs and the size presets under them land
   * in setSpecificDevice.
   *
   * A DOM flag rather than React state because the element it applies to is the body-level
   * canvas iframe, which React does not own. The timeout is the transition's own duration
   * plus a small margin, so the class is gone before anything else can resize the frame.
   */
  private deviceSwitchTimer: number | undefined;

  private armDeviceSwitchTransition = (): void => {
    if (typeof document === "undefined") return;
    const canvas: HTMLElement | null = document.getElementById("kuika_iframeAgent_canvas");
    if (!canvas) return;
    canvas.dataset.kuikaDeviceSwitch = "true";
    if (this.deviceSwitchTimer !== undefined) {
      window.clearTimeout(this.deviceSwitchTimer);
    }
    this.deviceSwitchTimer = window.setTimeout((): void => {
      delete canvas.dataset.kuikaDeviceSwitch;
      this.deviceSwitchTimer = undefined;
    }, 420);
  };

  setSpecificDevice = (device: IDevice) => {
    const deviceType: DeviceType = this.getSelectedDeviceTypeBySpecificDevice(device);
    // Only animate a REAL change — re-picking the current device would otherwise arm the
    // transition for a resize that never happens.
    if (this.state.selectedDevice?.id !== device.id) {
      this.armDeviceSwitchTransition();
    }
    this.setState({ selectedDevice: device, selectedDeviceType: deviceType });
  };

  getSelectedDeviceTypeBySpecificDevice(device: IDevice): DeviceType {
    let result: DeviceType = DeviceType.Desktop;
    switch (device.type) {
      case "Phone": {
        result = DeviceType.Phone;
        break;
      }
      case "Tablet": {
        result = DeviceType.Tablet;
        break;
      }
      case "Desktop": {
        result = DeviceType.Desktop;
        break;
      }
      default:
        result = DeviceType.Desktop;
        break;
    }
    return result;
  }

  setDeviceType = (deviceType: DeviceType) => {
    const key: string = this.getDefaultDeviceKeyByType(deviceType);
    if (this.isSelectedDeviceType(deviceType) === false) {
      this.setDeviceByKey(key);
    }
  };

  setDashboardState = (state: DashboardState) => {
    // Report Designer kill-switch: even non-UI entry paths (project-load state
    // restore, iframe postMessage, footer, MCP navigation) funnel through here, so
    // coerce any attempt to enter report-designer mode back to design when disabled.
    if (state === DashboardState.reportDesigner && !isReportDesignerEnabled()) {
      state = DashboardState.design;
    }
    const previousDashboardState: DashboardState = this.state.dashboardState;

    if (this.state.dashboardState === DashboardState.workFlow && state === DashboardState.workFlow) {
      state = DashboardState.design;
    } else if (this.state.dashboardState === DashboardState.configManager && state === DashboardState.configManager) {
      state = DashboardState.design;
    } else if (
      this.state.dashboardState === DashboardState.notificationTemplate &&
      state === DashboardState.notificationTemplate
    ) {
      state = DashboardState.design;
    } else if (this.state.dashboardState === state) {
      return;
    }
    if (state === DashboardState.design) {
      this.setState({ designViewLastClickedRightPanel: RightPanelType.Properties });
    }

    // KOA-17652 — re-render the canvas agent when re-entering an editable canvas.
    //
    // The design / report-designer canvas reuses the shared agent iframe, and the agent
    // enables drag & drop / element editing only while its `window.kuika.dashboardState`
    // is `design` or `reportDesigner`. The Branching flow repaints that same iframe in a
    // non-editable mode: activating or switching a branch runs `openProject` while the
    // dashboard is in `branching` state, so the renderFrame it emits carries
    // `dashboardState: branching`. A dashboard-state change on its own never pushes a
    // renderFrame, so on the way back the agent stays in that stale non-editable render
    // and drag & drop stays dead until the project is reopened.
    //
    // Pushing a fresh renderFrame whenever we enter an editable canvas from a non-editable
    // state re-applies the correct dashboardState to the agent — matching exactly what the
    // reopen-the-project workaround does (an `openProject` → `LoadProject` renderFrame).
    const isEnteringEditableCanvas: boolean =
      this.isEditableCanvasState(state) && !this.isEditableCanvasState(previousDashboardState);

    // The Document Parameters popup belongs to the surface it was opened from (KOA-17254). Anything that navigates
    // the dashboard has moved out from under it, and a dialog left floating over a place it was not opened from is
    // both confusing and, for the paths that navigate *in order to* show an editor, actively covering the thing the
    // user just asked for. The actions list inside the popup contains such a path: AI Agent authoring switches to
    // the data diagram to render its editor.
    this.setState({ isDocumentParametersPopupOpen: false });

    // Entering the full-page Helpdesk view. Every path that opens it — toolbar, iframe
    // postMessage, footer, shortcuts, the access-limited modal's "Contact us" — funnels through
    // this method, so this is the one place to reset it, not `ZendeskCanvas.componentDidMount`:
    // that component is shared with the Helpdesk MODAL, where it also remounts on every
    // Board→Inbox tab switch and must leave whatever ticket that switch just selected alone.
    if (state === DashboardState.zendesk) {
      this.partial.helpdeskProvider.openHelpdesk();
    }

    this.setState({ dashboardState: state }, (): void => this.afterToolSwitch(state, isEnteringEditableCanvas));
  };

  /**
   * The Report Designer is showing a report on its canvas — the tool is open and its landing is not.
   * Derived through `isReportDesignerLandingVisible`, never read off the landing flag alone, so a UI
   * screen still loaded under the tool keeps the report chrome down with it.
   */
  private isReportCanvasShown = (): boolean =>
    this.state.dashboardState === DashboardState.reportDesigner &&
    !this.partial.reportDesignProvider.isReportDesignerLandingVisible();

  /** The design and report-designer canvases are the two the agent iframe lets the user edit. */
  private isEditableCanvasState = (state: DashboardState): boolean =>
    state === DashboardState.design || state === DashboardState.reportDesigner;

  /**
   * What every tool switch does once the new dashboard state has landed — shared by
   * setDashboardState and enterReportDesigner so the two doors cannot drift apart.
   */
  private afterToolSwitch = (state: DashboardState, isEnteringEditableCanvas: boolean): void => {
    // Designer-live (Faz 5): the single tool-changed presence signal — every switch path
    // (toolbar, iframe, MCP, shortcuts, project load) funnels through this mutator. Tool
    // switches also close the previous tool's editors, so deferred reloads apply here.
    DesignerPresenceReporter.reportTool(state);
    notifyDesignerLiveToolIdle();
    if (!isEnteringEditableCanvas) {
      return;
    }
    // Only re-sync the agent once a screen design is actually loaded: renderFrame
    // dereferences screenDesign.displayItems, so firing it before the project/screen
    // has finished loading (empty screen design) would throw. When loaded, this is
    // exactly the re-render the reopen-the-project workaround performs.
    const screenDesign: IScreenDesign = this.partial.screenProvider.getScreenDesign();
    const screenIsLoaded: boolean = this.state.isProjectLoaded === true && Array.isArray(screenDesign?.displayItems);
    if (screenIsLoaded) {
      this.partial.frameProvider.renderFrame(ESdChangeReason.LoadProject);
    }
  };

  setIsCreateDataDiagram = (val: boolean) => {
    this.setState({ isCreateDataDiagram: val });
  };

  setTypographyStyleShow = (val: boolean) => {
    this.setState({ isTypographyStyleShow: val });
  };

  setBorderStyleShow = (val: boolean) => {
    this.setState({ isBorderStyleShow: val });
  };

  setShadowStyleShow = (val: boolean) => {
    this.setState({ isShadowStyleShow: val });
  };

  handleHideOrShowToolbar = (val: boolean) => {
    this.setState({ toolbarIsHidden: val });
  };

  setStylingActiveKeys = (newKeys: string[]) => {
    this.setState({ stylingActiveKeys: newKeys });
  };

  shouldRenderLeftMenu = (): boolean => {
    return true;
  };

  shouldRenderLeftPanel = (): boolean => {
    return !this.state.isLeftPanelCollapsed[this.state.dashboardState];
  };

  shouldRenderRightPanel = (): boolean => {
    const hasAccess = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.CreateComponent,
      this.state.project?.projectAccess
    );
    return !this.state.isRightPanelCollapsed[this.state.dashboardState] && hasAccess;
  };

  shouldRenderRightMenu = (): boolean => {
    return true;
  };

  shouldRenderToolBar = (): boolean => {
    return true;
  };

  shouldRenderFooter = (): boolean => {
    return true;
  };

  updateDatabaseDiagramData = async () => {
    const databaseDiagramData = {} as IDatabaseDiagramData;
    this.setState({ isDatabaseDiagramDataLoading: true }, async () => {
      try {
        const res = await DataDiagramSchemeService.GetLatestTypeSystemSchemaAndDiagram();
        if (res && res.data && res.status === 200) {
          databaseDiagramData.tableMenudata = res.data.diagramJson;
          databaseDiagramData.leftMenuData = res.data.schema;
          databaseDiagramData.error = undefined;
        }
      } catch (error: any) {
        databaseDiagramData.error = error?.response?.data?.message;
      }
      this.setState({ databaseDiagramData, isDatabaseDiagramDataLoading: false });
    });
  };

  setNotificationTemplateActivePanel = (key: string) => {
    this.setState({ notificationTemplateActivePanel: key });
  };

  setActiveZendeskPanel = (key: string) => {
    this.setState({ zendeskActivePanel: key });
  };

  /**
   * The full-page Helpdesk on the Kuika ITSM: its centre carries its own ticket list and ticket,
   * so the Zendesk ticket panel (left) and the Zendesk links panel (right) do not belong beside it.
   * Until the helpdesk config has answered this is false, which keeps the Zendesk layout as it was.
   */
  private isItsmHelpdeskShown = (): boolean =>
    this.state.dashboardState === DashboardState.zendesk && loadedHelpdeskProvider() === "Itsm";

  getVisibility = (viewPart: ViewPart): Visibility => {
    let result: Visibility = Visibility.Hidden;
    // Report Designer kill-switch (REPORT_DESIGNER_ENABLED=false): hide every entry
    // point into the report designer so it behaves as if the feature does not exist.
    // Kept in lockstep with the backend codegen, which then omits iText7 entirely.
    if (!isReportDesignerEnabled() && REPORT_DESIGNER_VIEW_PARTS.includes(viewPart)) {
      return Visibility.Hidden;
    }
    if (ITSM_HELPDESK_HIDDEN_VIEW_PARTS.includes(viewPart) && this.isItsmHelpdeskShown()) {
      return Visibility.Hidden;
    }
    switch (viewPart) {
      case ViewPart.LeftMenu: {
        if (
          this.shouldRenderLeftMenu() &&
          (this.state.dashboardState === DashboardState.design ||
            this.state.dashboardState === DashboardState.workFlow ||
            this.state.dashboardState === DashboardState.readOnly ||
            this.state.dashboardState === DashboardState.dataDiagramScheme ||
            this.state.dashboardState === DashboardState.aiHub ||
            this.state.dashboardState === DashboardState.configManager ||
            this.state.dashboardState === DashboardState.notificationTemplate ||
            this.state.dashboardState === DashboardState.scheduledJobs ||
            this.state.dashboardState === DashboardState.documentParameters ||
            this.state.dashboardState === DashboardState.apiManagement ||
            this.state.dashboardState === DashboardState.zendesk ||
            this.state.dashboardState === DashboardState.reportDesigner ||
            this.state.dashboardState === DashboardState.branching ||
            this.state.dashboardState === DashboardState.runSettings)
        ) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.LeftPanel: {
        // The workflow editor has no RESIDENT left panel: the create gallery is a
        // full-bleed landing screen, and inside a process the header's switcher took
        // over process navigation while the Parameters tab took over the fields
        // editor. The one exception is the datasources quick panel, opened on demand
        // from the icon rail and closed by clicking its rail button again (see
        // collapseLeftPanel). It mounts full-height beside the shell like on
        // the design screen; the secondary toolbar holds its centred tab strip in
        // place while the panel is open (see secondary-toolbar's `--centre-held`).
        // Gated here rather than as CSS so every consumer agrees.
        if (this.state.dashboardState === DashboardState.workFlow) {
          if (this.getActiveLeftPanel() === LeftPanelType.Datasources) {
            result = Visibility.Visible;
          }
          break;
        }
        // Scheduled Jobs has no RESIDENT left panel either: its list is the centre
        // landing page, so a panel beside it would be the same jobs twice. The
        // datasources quick panel still opens on demand from the icon rail — a job's
        // actions are database actions, and the one you want may not exist yet.
        // `shouldRenderLeftPanel` is asked here for the same reason every resident panel
        // asks it: the panel carries the usual edge collapse button, so the flag it writes
        // has to be the flag this gate reads. Leaving it out is what made collapse a no-op
        // while STILL raising the centre column's expand tab beside the panel that never
        // closed — two collapse controls at once (KOA-18379).
        if (this.state.dashboardState === DashboardState.scheduledJobs) {
          if (this.shouldRenderLeftPanel() && this.getActiveLeftPanel() === LeftPanelType.Datasources) {
            result = Visibility.Visible;
          }
          break;
        }
        // The API Builder has no RESIDENT left panel either: its APIs are the centre
        // landing page and the editor header's switcher moves between them, so a panel
        // beside it would be the same list twice. The datasources quick panel still opens
        // on demand — a method's actions are database actions, and the one you want may
        // not exist yet. It reads the collapse flag for the reason given above.
        if (this.state.dashboardState === DashboardState.apiManagement) {
          if (this.shouldRenderLeftPanel() && this.getActiveLeftPanel() === LeftPanelType.Datasources) {
            result = Visibility.Visible;
          }
          break;
        }
        if (
          this.shouldRenderLeftPanel() &&
          // `workFlow` is intentionally absent — it returned Hidden above.
          (this.state.dashboardState === DashboardState.design ||
            this.state.dashboardState === DashboardState.readOnly ||
            this.state.dashboardState === DashboardState.dataDiagramScheme ||
            // `aiHub` is intentionally absent: AI Functions lists everything in its
            // centre table — folders included — so a left panel would be a second copy
            // of the same list. Its left MENU (the icon rail) stays.
            //
            // `configManager` is intentionally absent for the same reason: the Config
            // Manager lists every configuration in its centre table, both scopes of it,
            // and the editor header's switcher moves between them. As with the two below,
            // `getActiveLeftPanel`'s mapping for this state must STAY — without it the
            // rail falls back to highlighting Components.
            //
            // `notificationTemplate` is intentionally absent: Email Builder lists every
            // template in its centre table, and the editor header's switcher moves between
            // them, so a left panel would be a second copy of the same list. Its left MENU
            // (the icon rail) stays, and so must `getActiveLeftPanel`'s mapping for this
            // state — without it the rail falls back to highlighting Components.
            // `scheduledJobs` is intentionally absent for the same reason, and returned
            // early above: its jobs list is the centre landing page, and the editor
            // header's switcher moves between jobs.
            // The tool has no sub-entity list of its own, but it does need the datasources panel: source actions
            // are database actions, and the one you want may not exist yet. Without this the only way to create it
            // was to leave the tool.
            // `apiManagement` is intentionally absent for the same reason, and returned
            // early above: its API list is the centre landing page, and the editor
            // header's switcher moves between APIs.
            this.state.dashboardState === DashboardState.documentParameters ||
            // The Report Designer keeps a resident panel, but not on its LANDING page: that
            // page lists every report in its own table, so the panel beside it would be a
            // second copy of the same list — the reason `aiHub` and `notificationTemplate` are
            // absent from this list outright. It returns the moment a report opens, because
            // unlike those two this panel is the Screens panel in report mode, and it is how
            // you move between reports while editing one.
            this.isReportCanvasShown() ||
            // `branching` is intentionally absent: the branching workspace has no resident
            // left panel — the branch list is embedded as the workspace card's own sidebar
            // (BranchingCanvas renders BranchingPanel inside the card).
            this.state.dashboardState === DashboardState.zendesk)
        ) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.Toolbar: {
        if (this.shouldRenderToolBar()) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.SecondaryToolbar: {
        // Workflow only. The design and report dashboards get their row-2 from the shell's
        // `DesignSubBar` (KOA-16942), gated inline by dashboard state; this ViewPart now
        // answers one question — does the workflow band (the process switcher and tab strip,
        // moved here out of the process editor) belong on screen?
        //
        // Gated on *not* being on the create landing page: while the "Create a New Process"
        // gallery is up there is no open process to switch or tab through, and the band must
        // not appear over it.
        if (this.state.dashboardState === DashboardState.workFlow && this.state.isCreateWfActive === false) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.Footer: {
        if (
          this.shouldRenderFooter() &&
          (this.state.dashboardState === DashboardState.design ||
            this.state.dashboardState === DashboardState.workFlow ||
            this.state.dashboardState === DashboardState.dataDiagramScheme ||
            this.state.dashboardState === DashboardState.aiHub ||
            this.state.dashboardState === DashboardState.configManager ||
            this.state.dashboardState === DashboardState.notificationTemplate ||
            this.state.dashboardState === DashboardState.scheduledJobs ||
            this.state.dashboardState === DashboardState.documentParameters ||
            this.state.dashboardState === DashboardState.apiManagement ||
            this.state.dashboardState === DashboardState.reportDesigner ||
            this.state.dashboardState === DashboardState.zendesk ||
            this.state.dashboardState === DashboardState.branching ||
            this.state.dashboardState === DashboardState.runSettings)
        ) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.FooterBreadcrumb: {
        if (
          this.shouldRenderFooter() &&
          (this.state.dashboardState === DashboardState.design ||
            this.state.dashboardState === DashboardState.reportDesigner)
        ) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.Devices: {
        // Deliberately NOT `reportDesigner`: a report is sized by paper, not by viewport, so
        // offering it a phone/tablet device is meaningless (it has the page-size group instead).
        // The Report Designer entry that used to sit in this list was removed with it — the
        // sub-bar renders the selector on Design only, and this contract now says the same.
        if (
          this.state.dashboardState === DashboardState.design ||
          this.state.dashboardState === DashboardState.readOnly
        ) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.ReportSizesDropdown: {
        if (this.state.dashboardState === DashboardState.reportDesigner) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.RightPanel: {
        // Narrowed with the Explore Documentation removal: states whose panel content was
        // documentation-only (configManager, runSettings, notificationTemplate) left the list
        // entirely, and the selection-driven states show the panel only once something is
        // selected — otherwise the old docs default would now be an empty frame.
        if (
          this.shouldRenderRightPanel() &&
          (this.state.dashboardState === DashboardState.design ||
            // Only once a workflow is actually open (selection-driven, like the other states
            // below). The create-workflow screen (gallery, `isCreateWfActive`, no `selectedWf`)
            // has no component to inspect, so it shows no right panel — it used to leak through
            // because `!selectedWfIsReadOnly` is truthy when nothing is selected.
            (this.state.dashboardState === DashboardState.workFlow &&
              !!this.state.selectedWf?.id &&
              !this.state.selectedWfIsReadOnly) ||
            (this.state.dashboardState === DashboardState.scheduledJobs && !!this.state.selectedSjId) ||
            // Not while the Document Parameters tool is hosting the database action editor: that editor is the data
            // diagram's, and the data diagram gives it no right panel at all. Keeping the shell up left the tool's
            // own Document Inputs list standing beside an action that has nothing to do with it.
            (this.state.dashboardState === DashboardState.documentParameters &&
              !this.state.isEditingDocumentParameterSourceAction) ||
            (this.state.dashboardState === DashboardState.apiManagement && !!this.state.activeApiMethodId) ||
            this.isReportCanvasShown() ||
            ((this.state.dashboardState === DashboardState.dataDiagramScheme ||
              this.state.dashboardState === DashboardState.aiHub) &&
              (((this.state.activeCreatingDatasourceType === EDatasourceType.BleService ||
                this.state.activeEditingDatasource?.datasourceType === EDatasourceType.BleService) &&
                !!this.state.activeBluetoothMethodId) ||
                (this.state.creatingActionsDatasource?.datasourceType === EDatasourceType.AIActions &&
                  this.state.activeAIActionInfoForRightPanel))) ||
            this.state.dashboardState === DashboardState.zendesk)
        ) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.RightMenu: {
        // Retired by the layout redesign: the avatar it carried now sits in the primary
        // toolbar (next to the project/workspace button), and the right panel switches
        // itself through its own tabs. Hidden rather than deleted.
        //
        // To bring it back, restore this condition (kept verbatim so the reversal is exact;
        // `shouldRenderRightMenu()` is retained for the same reason):
        //
        //   this.shouldRenderRightMenu() &&
        //     (workFlow || dataDiagramScheme || aiHub || configManager || notificationTemplate ||
        //      scheduledJobs || documentParameters || apiManagement || zendesk || runSettings)
        //
        // Nothing is orphaned by this. The Comments button it hosted was already
        // unreachable: it is gated to `design`, which this list never included, so it
        // rendered in no state at all. The Style and Documents buttons were gated the same
        // way (design / report designer) and have been deleted. Their PANELS are unaffected —
        // those are in the right panel and are opened programmatically (datasource-provider
        // and the MCP navigation tool), not from this rail. Workspace screens have their own top menu
        // and never rendered this component.
        result = Visibility.Hidden;
        break;
      }
      case ViewPart.ScreensPanel: {
        if (this.getActiveLeftPanel() === LeftPanelType.Screens) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.Audit: {
        if (KMainFunctions.isMainAI()) break;

        if (this.getActiveLeftPanel() === LeftPanelType.Audit) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.ReportDesignerPagesPanel: {
        // Hidden on the landing page for the same reason the panel itself is (see
        // ViewPart.LeftPanel); the two must agree, or the shell holds a column open for a
        // panel that renders nothing.
        if (
          this.getActiveLeftPanel() === LeftPanelType.Reports &&
          !this.partial.reportDesignProvider.isReportDesignerLandingVisible()
        ) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.ComponentsPanel: {
        if (
          this.state.dashboardState === DashboardState.design &&
          this.getActiveLeftPanel() === LeftPanelType.Component
        ) {
          result = Visibility.Visible;
        } else if (
          this.state.dashboardState === DashboardState.reportDesigner &&
          this.getActiveLeftPanel() === LeftPanelType.Component
        ) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.ActionTreeButton: {
        if (this.state.dashboardState === DashboardState.design) {
          result = Visibility.Visible;
        }

        break;
      }
      case ViewPart.ActionTreePanel: {
        if (
          this.state.dashboardState === DashboardState.design &&
          this.getActiveLeftPanel() === LeftPanelType.ActionTree
        ) {
          result = Visibility.Visible;
        }

        break;
      }
      case ViewPart.DatasourcesPanel: {
        if (this.getActiveLeftPanel() === LeftPanelType.Datasources) {
          result = Visibility.Visible;
        } else if (
          this.state.dashboardState === DashboardState.reportDesigner &&
          this.getActiveLeftPanel() === LeftPanelType.Datasources
        ) {
          result = Visibility.Visible;
        } else if (
          this.state.dashboardState === DashboardState.scheduledJobs &&
          this.getActiveLeftPanel() === LeftPanelType.Datasources
        ) {
          result = Visibility.Visible;
        } else if (
          this.state.dashboardState === DashboardState.apiManagement &&
          this.getActiveLeftPanel() === LeftPanelType.Datasources
        ) {
          result = Visibility.Visible;
        } else if (
          this.state.dashboardState === DashboardState.workFlow &&
          this.getActiveLeftPanel() === LeftPanelType.Datasources
        ) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.ImageLibraryPanel: {
        if (
          this.state.dashboardState === DashboardState.design &&
          this.getActiveLeftPanel() === LeftPanelType.ImageLibrary
        ) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.LanguagePanel: {
        if (
          (this.state.dashboardState === DashboardState.design ||
            this.state.dashboardState === DashboardState.reportDesigner) &&
          this.getActiveLeftPanel() === LeftPanelType.Language
        ) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.CodeTreePanel: {
        if (this.getActiveLeftPanel() === LeftPanelType.CodeTree) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.DataDiagramSchemePanel: {
        if (this.getActiveLeftPanel() === LeftPanelType.DataDiagramScheme) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.WorkFlowListPanel: {
        if (this.getActiveLeftPanel() === LeftPanelType.WorkFlowList) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.WorkFlowFieldListPanel: {
        if (this.getActiveLeftPanel() === LeftPanelType.WorkFlowFieldList) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.ConfigManagerPanel: {
        // Never visible: the Configurations panel was folded into the Config Manager's
        // centre table. This has to agree with `ViewPart.LeftPanel` above, or the shell
        // holds a column open for a panel that renders nothing. The ViewPart itself stays —
        // it is a serialised enum, and the search-text store is still keyed by it.
        break;
      }
      case ViewPart.ApiManagementPanel: {
        // Never visible: the API's panel was folded into the API Builder's centre table.
        // The ViewPart itself stays — it is a serialised enum, and the search-text store is
        // still keyed by it.
        break;
      }
      case ViewPart.NotificationTemplatePanel: {
        // Never visible: the Emails panel was folded into the Email Builder's centre table.
        // The ViewPart itself stays — it is a serialised enum, and the search-text store is
        // still keyed by it.
        break;
      }
      case ViewPart.ZendeskPanel: {
        if (this.getActiveLeftPanel() === LeftPanelType.Zendesk) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.DisplayItemTreePanel: {
        if (
          (this.state.dashboardState === DashboardState.design ||
            this.state.dashboardState === DashboardState.reportDesigner) &&
          this.getActiveLeftPanel() === LeftPanelType.DisplayItemTree
        ) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.PropertiesPanel: {
        if (this.getActiveRightPanel() === RightPanelType.Properties) {
          result = Visibility.Visible;
        }
        break;
      }

      case ViewPart.SdPropertiesPanel: {
        if (
          this.getActiveRightPanel() === RightPanelType.Properties &&
          (this.state.dashboardState === DashboardState.design ||
            this.state.dashboardState === DashboardState.reportDesigner)
        ) {
          result = Visibility.Visible;
        }
        break;
      }

      case ViewPart.ChatbotButton: {
        if (this.state.dashboardState === DashboardState.design) {
          result = Visibility.Visible;
        }
        break;
      }

      case ViewPart.ChatbotPanel: {
        if (this.getActiveRightPanel() === RightPanelType.Chatbot) {
          result = Visibility.Visible;
        }

        break;
      }
      case ViewPart.ZendeskPropertiesPanel: {
        if (this.state.dashboardState === DashboardState.zendesk) {
          result = Visibility.Visible;
        }

        break;
      }

      case ViewPart.WorkflowRightPanel: {
        if (this.state.dashboardState === DashboardState.workFlow) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.WfPropertiesPanel: {
        if (
          (this.getActiveRightPanel() === RightPanelType.Properties ||
            this.getActiveRightPanel() === RightPanelType.AdvancedProperties) &&
          this.state.dashboardState === DashboardState.workFlow
        ) {
          result = Visibility.Visible;
        }
        break;
      }

      // ViewPart.HappyPathButton / HappyPathPropertiesPanel were retired: Happy Path phases are
      // managed entirely on the diagram as native bpmn:Group columns (happyPathPhaseInterceptor).
      // The enum members stay in kuika-cl-model for compatibility; nothing renders them anymore.

      case ViewPart.WorkflowVersionButton: {
        if (this.state.dashboardState === DashboardState.workFlow && this.state.selectedWf) {
          result = Visibility.Visible;
        }
        break;
      }

      case ViewPart.WorkflowVersionPropertiesPanel: {
        if (
          this.getActiveRightPanel() === RightPanelType.WorkflowVersion &&
          this.state.dashboardState === DashboardState.workFlow
        ) {
          result = Visibility.Visible;
        }
        break;
      }

      case ViewPart.ScheduledJobsPropertiesPanel: {
        if (
          this.getActiveRightPanel() === RightPanelType.Properties &&
          this.state.dashboardState === DashboardState.scheduledJobs
        ) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.NotificationTemplatePropertiesPanel: {
        if (
          this.getActiveRightPanel() === RightPanelType.Properties &&
          this.state.dashboardState === DashboardState.notificationTemplate
        ) {
          result = Visibility.Visible;
        }
        break;
      }

      case ViewPart.DocumentParametersPropertiesPanel: {
        // No sub-entity to select: the tool itself is the host, so the action list is available as soon as the
        // surface is open — the same relationship PageInit has with a screen. The exception is the tool's in-place
        // action editor, which takes the whole surface; the same condition gates `ViewPart.RightPanel`, and the
        // two are kept in step because a panel that claims to be visible inside a hidden shell is exactly the kind
        // of disagreement that later gets "fixed" in the wrong place.
        if (
          this.state.dashboardState === DashboardState.documentParameters &&
          !this.state.isEditingDocumentParameterSourceAction
        ) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.ApiManagementPropertiesPanel: {
        if (
          this.getActiveRightPanel() === RightPanelType.Properties &&
          this.state.dashboardState === DashboardState.apiManagement
        ) {
          result = Visibility.Visible;
        }
        break;
      }

      case ViewPart.StylingPanel: {
        if (this.getActiveRightPanel() === RightPanelType.Styling) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.DocumentsPanel: {
        if (this.getActiveRightPanel() === RightPanelType.Documents) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.CommentsPanel: {
        if (this.getActiveRightPanel() === RightPanelType.Comments) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.HistoryPanel: {
        if (this.getActiveRightPanel() === RightPanelType.History) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.RolePanel: {
        if (this.state.dashboardState === DashboardState.design) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.AuthorizationPanel: {
        if (
          this.state.dashboardState === DashboardState.design ||
          this.state.dashboardState === DashboardState.apiManagement
        ) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.PressEnterPanel: {
        if (
          this.state.screenList?.find((s) => s.id.toString() === this.state.sdId.toString())?.publicMetaData
            ?.screenType !== EScreenType.Master &&
          this.state.dashboardState === DashboardState.design
        ) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.ValidationPanel: {
        if (this.state.dashboardState === DashboardState.design) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.ScreensButton: {
        if (this.state.dashboardState === DashboardState.design) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.AuditButton: {
        if (KMainFunctions.isMainAI()) break;

        if (this.state.dashboardState === DashboardState.design) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.ReportsButton: {
        if (this.state.dashboardState === DashboardState.reportDesigner) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.ComponentsButton: {
        if (this.state.dashboardState === DashboardState.design || this.isReportCanvasShown()) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.ImageLibraryButton: {
        if (this.state.dashboardState === DashboardState.design) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.DataDiagramSchemeButton: {
        if (
          this.state.dashboardState === DashboardState.design ||
          this.state.dashboardState === DashboardState.reportDesigner
        ) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.LanguageButton: {
        if (this.state.dashboardState === DashboardState.design) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.DatasourcesButton: {
        // Not in documentParameters: that tool's left panel is the sources tree, and nothing there switches back to
        // it, so this button would be a one-way trip out of the panel the tool is entered from. Action creation is
        // not lost — the sources tree offers it per datasource.
        if (
          this.state.dashboardState === DashboardState.design ||
          this.isReportCanvasShown() ||
          this.state.dashboardState === DashboardState.scheduledJobs ||
          this.state.dashboardState === DashboardState.apiManagement ||
          this.state.dashboardState === DashboardState.workFlow
        ) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.DisplayItemTreeButton: {
        if (this.state.dashboardState === DashboardState.design || this.isReportCanvasShown()) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.PropertiesButton: {
        // Intentionally not visible in the workFlow state: the workflow right panel is always shown on the
        // Diagram tab (Happy Path / Version toggles were retired), so the button had nothing left to toggle.
        // Panel visibility is independent of this button — ViewPart.WfPropertiesPanel keys off
        // getActiveRightPanel(), which for workFlow returns workFlowViewLastClickedRightPanel (initialised to
        // RightPanelType.Properties and re-set to it on every workflow-view entry / process switch).
        if (this.state.dashboardState === DashboardState.design || this.isReportCanvasShown()) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.CommentsButton: {
        if (this.state.dashboardState === DashboardState.design) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.HistoryButton: {
        if (
          this.state.dashboardState === DashboardState.design ||
          this.state.dashboardState === DashboardState.dataDiagramScheme
        ) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.SaveButton: {
        // NOT IMPLEMENTED YET
        break;
      }
      case ViewPart.UndoRedo: {
        // NOT IMPLEMENTED YET
        break;
      }
      case ViewPart.Phone: {
        // NOT IMPLEMENTED YET
        break;
      }
      case ViewPart.Tablet: {
        // NOT IMPLEMENTED YET
        break;
      }
      case ViewPart.Desktop: {
        // NOT IMPLEMENTED YET
        break;
      }
      case ViewPart.PreviewButton: {
        // NOT IMPLEMENTED YET
        break;
      }
      case ViewPart.WorkFlowButton: {
        if (
          this.state.dashboardState === DashboardState.design ||
          this.state.dashboardState === DashboardState.workFlow ||
          this.state.dashboardState === DashboardState.dataDiagramScheme ||
          this.state.dashboardState === DashboardState.aiHub ||
          this.state.dashboardState === DashboardState.configManager ||
          this.state.dashboardState === DashboardState.notificationTemplate ||
          this.state.dashboardState === DashboardState.scheduledJobs ||
          this.state.dashboardState === DashboardState.documentParameters ||
          this.state.dashboardState === DashboardState.apiManagement ||
          this.state.dashboardState === DashboardState.zendesk ||
          this.state.dashboardState === DashboardState.reportDesigner ||
          this.state.dashboardState === DashboardState.runSettings ||
          this.state.dashboardState === DashboardState.branching
        ) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.WorkFlowListButton: {
        if (this.state.dashboardState === DashboardState.workFlow) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.WorkFlowFieldListButton: {
        if (this.state.dashboardState === DashboardState.workFlow) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.ConfigManagerButton: {
        if (
          this.state.dashboardState === DashboardState.design ||
          this.state.dashboardState === DashboardState.workFlow ||
          this.state.dashboardState === DashboardState.dataDiagramScheme ||
          this.state.dashboardState === DashboardState.aiHub ||
          this.state.dashboardState === DashboardState.configManager ||
          this.state.dashboardState === DashboardState.scheduledJobs ||
          this.state.dashboardState === DashboardState.documentParameters ||
          this.state.dashboardState === DashboardState.apiManagement ||
          this.state.dashboardState === DashboardState.zendesk ||
          this.state.dashboardState === DashboardState.reportDesigner ||
          this.state.dashboardState === DashboardState.runSettings ||
          this.state.dashboardState === DashboardState.branching
        ) {
          result = Visibility.Visible;
        }
      }
      case ViewPart.ScheduledJobsButton: {
        if (
          this.state.dashboardState === DashboardState.design ||
          this.state.dashboardState === DashboardState.workFlow ||
          this.state.dashboardState === DashboardState.dataDiagramScheme ||
          this.state.dashboardState === DashboardState.aiHub ||
          this.state.dashboardState === DashboardState.configManager ||
          this.state.dashboardState === DashboardState.notificationTemplate ||
          this.state.dashboardState === DashboardState.scheduledJobs ||
          this.state.dashboardState === DashboardState.documentParameters ||
          this.state.dashboardState === DashboardState.apiManagement ||
          this.state.dashboardState === DashboardState.zendesk ||
          this.state.dashboardState === DashboardState.reportDesigner ||
          this.state.dashboardState === DashboardState.runSettings ||
          this.state.dashboardState === DashboardState.branching
        ) {
          result = Visibility.Visible;
        }
      }
      case ViewPart.ApiManagementButton: {
        if (
          this.state.dashboardState === DashboardState.design ||
          this.state.dashboardState === DashboardState.workFlow ||
          this.state.dashboardState === DashboardState.dataDiagramScheme ||
          this.state.dashboardState === DashboardState.aiHub ||
          this.state.dashboardState === DashboardState.configManager ||
          this.state.dashboardState === DashboardState.notificationTemplate ||
          this.state.dashboardState === DashboardState.scheduledJobs ||
          this.state.dashboardState === DashboardState.documentParameters ||
          this.state.dashboardState === DashboardState.apiManagement ||
          this.state.dashboardState === DashboardState.zendesk ||
          this.state.dashboardState === DashboardState.reportDesigner ||
          this.state.dashboardState === DashboardState.runSettings ||
          this.state.dashboardState === DashboardState.branching
        ) {
          result = Visibility.Visible;
        }
      }
      case ViewPart.NotificationTemplateButton: {
        if (
          this.state.dashboardState === DashboardState.design ||
          this.state.dashboardState === DashboardState.workFlow ||
          this.state.dashboardState === DashboardState.dataDiagramScheme ||
          this.state.dashboardState === DashboardState.aiHub ||
          this.state.dashboardState === DashboardState.configManager ||
          this.state.dashboardState === DashboardState.notificationTemplate ||
          this.state.dashboardState === DashboardState.scheduledJobs ||
          this.state.dashboardState === DashboardState.documentParameters ||
          this.state.dashboardState === DashboardState.apiManagement ||
          this.state.dashboardState === DashboardState.reportDesigner ||
          this.state.dashboardState === DashboardState.zendesk ||
          this.state.dashboardState === DashboardState.runSettings ||
          this.state.dashboardState === DashboardState.branching
        ) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.ReportDesignerButton: {
        if (
          this.state.dashboardState === DashboardState.design ||
          this.state.dashboardState === DashboardState.workFlow ||
          this.state.dashboardState === DashboardState.dataDiagramScheme ||
          this.state.dashboardState === DashboardState.aiHub ||
          this.state.dashboardState === DashboardState.configManager ||
          this.state.dashboardState === DashboardState.notificationTemplate ||
          this.state.dashboardState === DashboardState.scheduledJobs ||
          this.state.dashboardState === DashboardState.documentParameters ||
          this.state.dashboardState === DashboardState.apiManagement ||
          this.state.dashboardState === DashboardState.zendesk ||
          this.state.dashboardState === DashboardState.reportDesigner ||
          this.state.dashboardState === DashboardState.runSettings ||
          this.state.dashboardState === DashboardState.branching
        ) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.ZendeskButton: {
        if (
          this.state.dashboardState === DashboardState.design ||
          this.state.dashboardState === DashboardState.workFlow ||
          this.state.dashboardState === DashboardState.dataDiagramScheme ||
          this.state.dashboardState === DashboardState.configManager ||
          this.state.dashboardState === DashboardState.notificationTemplate ||
          this.state.dashboardState === DashboardState.scheduledJobs ||
          this.state.dashboardState === DashboardState.documentParameters ||
          this.state.dashboardState === DashboardState.apiManagement ||
          this.state.dashboardState === DashboardState.zendesk ||
          this.state.dashboardState === DashboardState.reportDesigner ||
          this.state.dashboardState === DashboardState.runSettings ||
          this.state.dashboardState === DashboardState.branching
        ) {
          result = Visibility.Visible;
        }
      }
      case ViewPart.ApiManagementPanelButton: {
        // Retired with the API's left panel: the rail button only toggled that panel, whose
        // list now IS the tool's landing page. The tool is reached from the header's tool
        // tabs. Never visible.
        break;
      }
      case ViewPart.BranchingPanelButton: {
        // Retired with the branch-list left panel: the rail button only toggled that panel,
        // which now lives inside the branching workspace card. Never visible.
        break;
      }
      case ViewPart.BranchingPanel: {
        // Never visible as a LEFT panel anymore: the branch list is embedded inside the
        // branching workspace card (BranchingCanvas → BranchingPanel), not the panel region.
        break;
      }
      case ViewPart.DesignButton: {
        // NOT IMPLEMENTED YET
        break;
      }
      case ViewPart.ShareButton: {
        // NOT IMPLEMENTED YET
        break;
      }
      case ViewPart.SendApproveButton: {
        // NOT IMPLEMENTED YET
        break;
      }
      case ViewPart.ApproveButton: {
        // NOT IMPLEMENTED YET
        break;
      }
      case ViewPart.SaveAndSaveAsMenuItems: {
        // NOT IMPLEMENTED YET
        break;
      }
      case ViewPart.ExportPngMenuItem: {
        // NOT IMPLEMENTED YET
        break;
      }
      case ViewPart.ExportKuikaFileMenuItem: {
        // NOT IMPLEMENTED YET
        break;
      }
      case ViewPart.DesignCanvas: {
        // NOT IMPLEMENTED YET
        break;
      }
      case ViewPart.WorkFlowDesign: {
        // NOT IMPLEMENTED YET
        break;
      }
      case ViewPart.MetadataViewer: {
        // NOT IMPLEMENTED YET
        break;
      }
      case ViewPart.RemoveScreenContentButton: {
        // NOT IMPLEMENTED YET
        break;
      }
      case ViewPart.VersiyonInfo: {
        // NOT IMPLEMENTED YET
        break;
      }
      case ViewPart.SortCutList: {
        // NOT IMPLEMENTED YET
        break;
      }
      case ViewPart.Zoom: {
        // NOT IMPLEMENTED YET
        break;
      }
      case ViewPart.BreadCrumb: {
        // NOT IMPLEMENTED YET
        break;
      }
      case ViewPart.DesignerButton: {
        if (
          this.state.dashboardState === DashboardState.design ||
          this.state.dashboardState === DashboardState.workFlow ||
          this.state.dashboardState === DashboardState.dataDiagramScheme ||
          this.state.dashboardState === DashboardState.aiHub ||
          this.state.dashboardState === DashboardState.configManager ||
          this.state.dashboardState === DashboardState.notificationTemplate ||
          this.state.dashboardState === DashboardState.scheduledJobs ||
          this.state.dashboardState === DashboardState.documentParameters ||
          this.state.dashboardState === DashboardState.apiManagement ||
          this.state.dashboardState === DashboardState.zendesk ||
          this.state.dashboardState === DashboardState.reportDesigner ||
          this.state.dashboardState === DashboardState.runSettings ||
          this.state.dashboardState === DashboardState.branching
        ) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.BluetoothPropertiesPanel: {
        if (
          this.state.dashboardState === DashboardState.dataDiagramScheme &&
          this.state.activeEditingDatasource?.datasourceType === EDatasourceType.BleService
        ) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.CustomAITemplatePropertiesPanel: {
        if (
          this.state.creatingActionsDatasource?.datasourceType === EDatasourceType.AIActions &&
          (this.state.editingAction?.actionType === EDatasourceType.AIActions ||
            this.state.editingAction?.aiActionInfo ||
            this.state.activeCreatingActionName)
        ) {
          result = Visibility.Visible;
        }
        break;
      }
      case ViewPart.AIChatRightPanel: {
        if (this.state.chatDockedToPanel === "right") {
          result = Visibility.Visible;
        }
        break;
      }
      default: {
        // NOT IMPLEMENTED YET
        break;
      }
    }
    return result;
  };
}
